/**
 * Supabase Write Queue — ensures writes reach the database even if they fail initially.
 *
 * When a Supabase write fails (network error, timeout, RLS block, etc.), the write
 * is queued in localStorage and retried on:
 *   - App load
 *   - Pull-to-refresh
 *   - Tab focus (visibility change)
 *
 * This replaces all fire-and-forget .catch(() => {}) patterns in the codebase.
 */

const QUEUE_KEY = 'pm_write_queue';
const MAX_RETRIES = 5;
const MAX_QUEUE_SIZE = 100; // Safety cap

/**
 * Add a failed write to the retry queue.
 * @param {string} type - Write type identifier (e.g. 'analysis', 'serviceLog', 'mileageLog', etc.)
 * @param {object} payload - The data needed to retry the write
 */
export function enqueueWrite(type, payload) {
  try {
    const queue = getQueue();
    // Check for duplicate (same type + same key fields)
    const isDuplicate = queue.some((item) =>
      item.type === type && JSON.stringify(item.payload) === JSON.stringify(payload)
    );
    if (isDuplicate) return;

    queue.push({
      type,
      payload,
      retries: 0,
      createdAt: new Date().toISOString(),
      lastAttempt: null,
    });

    // Cap queue size — drop oldest entries
    while (queue.length > MAX_QUEUE_SIZE) {
      queue.shift();
    }

    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    console.warn('[WriteQueue] Enqueued failed write:', type, '— queue size:', queue.length);
  } catch (err) {
    console.error('[WriteQueue] Failed to enqueue:', err.message);
  }
}

/**
 * Get the current write queue.
 * @returns {Array}
 */
export function getQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Save the queue back to localStorage.
 */
function saveQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (err) {
    console.error('[WriteQueue] Failed to save queue:', err.message);
  }
}

/**
 * Get the count of pending writes in the queue.
 * @returns {number}
 */
export function getPendingWriteCount() {
  return getQueue().length;
}

/**
 * Process the write queue — retry all pending writes.
 * Called on app load, pull-to-refresh, and visibility change.
 *
 * @param {object} handlers - Map of write type → async handler function
 * @returns {Promise<{succeeded: number, failed: number, dropped: number}>}
 */
export async function processQueue(handlers) {
  const queue = getQueue();
  if (queue.length === 0) return { succeeded: 0, failed: 0, dropped: 0 };

  console.log('[WriteQueue] Processing', queue.length, 'pending writes...');

  const remaining = [];
  let succeeded = 0;
  let failed = 0;
  let dropped = 0;

  for (const item of queue) {
    const handler = handlers[item.type];
    if (!handler) {
      console.warn('[WriteQueue] No handler for type:', item.type, '— dropping');
      dropped++;
      continue;
    }

    if (item.retries >= MAX_RETRIES) {
      console.error('[WriteQueue] Max retries exceeded for:', item.type, '— dropping:', item.payload);
      dropped++;
      continue;
    }

    try {
      await handler(item.payload);
      succeeded++;
    } catch (err) {
      console.error('[WriteQueue] Retry failed for:', item.type, '—', err.message);
      item.retries++;
      item.lastAttempt = new Date().toISOString();
      remaining.push(item);
      failed++;
    }
  }

  saveQueue(remaining);

  if (succeeded > 0 || dropped > 0) {
    console.log('[WriteQueue] Processed:', { succeeded, failed, dropped, remaining: remaining.length });
  }

  return { succeeded, failed, dropped };
}

/**
 * Clear the entire write queue. Use with caution.
 */
export function clearQueue() {
  localStorage.removeItem(QUEUE_KEY);
}

/**
 * Wrapper: attempt a Supabase write, enqueue on failure.
 * Replaces all .catch(() => {}) patterns.
 *
 * @param {string} type - Write type identifier
 * @param {object} payload - Data for retry
 * @param {Function} writeFn - Async function that performs the actual write
 * @returns {Promise<boolean>} - true if write succeeded, false if queued
 */
export async function reliableWrite(type, payload, writeFn) {
  try {
    await writeFn();
    return true;
  } catch (err) {
    console.error('[WriteQueue] Write failed, queuing for retry:', type, '—', err.message);
    enqueueWrite(type, payload);
    return false;
  }
}
